import { ComponentType } from 'react';

import dayjs from 'dayjs';

import { SECONDS_IN_HOUR, TIME_AMPM } from '@cast/constants';
import {
  ChartTooltipElementProps,
  DataPopperRow,
  theme,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { dashesOrNum, getCurrencyPrecision } from '@cast/utils';

import { Money } from 'components/money';
import { nodeTypeColors } from 'constants/maps';
import { useCostOverTime } from 'features/cost-report/hooks/useCostOverTime';
import {
  CostReportChartType,
  NormalizedCostChartData,
  NormalizedCostMode,
} from 'features/cost-report/types/costOverTime';
import { NodeType } from 'types/nodes';
import { granularityToSeconds } from 'utils/costReport';

import { useCostOverTimeSection } from '../../../../../cost-report/hooks/useCostOverTimeSection';
import { useCostReportContext } from '../../../../../cost-report/hooks/useCostReportContext';
import { useNormalizedCostsControls } from '../_hooks/useNormalizedCostControls';

const normalizedCostColors: Record<NormalizedCostMode, string | undefined> = {
  [NormalizedCostMode.NORMALIZED]: theme.palette.blue[700],
  [NormalizedCostMode.ON_DEMAND]: nodeTypeColors[NodeType.ON_DEMAND],
  [NormalizedCostMode.FALLBACK]: nodeTypeColors[NodeType.FALLBACK],
  [NormalizedCostMode.SPOT]: nodeTypeColors[NodeType.SPOT],
};

const normalizedCostModeMap: Record<NormalizedCostMode, string> = {
  [NormalizedCostMode.NORMALIZED]: 'Normalized',
  [NormalizedCostMode.ON_DEMAND]: 'On-demand',
  [NormalizedCostMode.FALLBACK]: 'Fallback',
  [NormalizedCostMode.SPOT]: 'Spot',
};

type MakeChartTooltipArgs = { unit: string };
type TooltipProps = ChartTooltipElementProps<NormalizedCostChartData>;
type MakeChartTooltipResult = {
  tooltipHeader: ComponentType<TooltipProps>;
  tooltipFooter: ComponentType<TooltipProps>;
};

const getForecastKey = (mode: NormalizedCostMode) =>
  `${mode}Forecast` as keyof NormalizedCostChartData;

export const makeChartTooltip = ({
  unit,
}: MakeChartTooltipArgs): MakeChartTooltipResult => {
  const TooltipHeader = ({ data, testId }: TooltipProps) => {
    const { activeTab } = useNormalizedCostsControls();
    const { forecast } = data;
    const { chartType, timezone, granularity } = useCostReportContext();
    const { key, projectEndOfDayCost } = useCostOverTime();
    if (activeTab === NormalizedCostMode.FALLBACK && forecast) {
      return null;
    }
    const timestamp = data.timestamp ?? '';
    const date = dayjs.utc(timestamp).tz(timezone).format('MMM D');
    const time =
      key === 'cluster' &&
      chartType === CostReportChartType.AREA &&
      granularityToSeconds(granularity) <= SECONDS_IN_HOUR
        ? dayjs.utc(timestamp).tz(timezone).format(TIME_AMPM)
        : undefined;

    const costValue = data[activeTab];
    const cost = typeof costValue === 'number' ? costValue : null;
    const projectedValue = data[getForecastKey(activeTab)];
    const isProjected =
      !forecast &&
      projectEndOfDayCost &&
      cost !== null &&
      typeof projectedValue === 'number' &&
      cost < projectedValue;

    return (
      <DataPopperRow
        testId={testId}
        left={
          <Typography
            variant="L10M"
            color={tokens.theme.fg.structural.secondary}
          >
            {date}
          </Typography>
        }
        right={
          forecast ? (
            <Typography
              variant="L10M"
              color={tokens.theme.fg.structural.secondary}
            >
              Forecast
            </Typography>
          ) : isProjected ? (
            <Typography
              variant="L10M"
              color={tokens.theme.fg.structural.secondary}
            >
              Projected
            </Typography>
          ) : time ? (
            <Typography
              variant="L10M"
              color={tokens.theme.fg.structural.secondary}
            >
              {time}
            </Typography>
          ) : undefined
        }
      />
    );
  };

  const TooltipFooter = ({ data }: TooltipProps) => {
    const { activeTab } = useNormalizedCostsControls();
    const { costRate } = useCostOverTimeSection();
    const { projectEndOfDayCost } = useCostOverTime();

    const costValue = data[activeTab];
    const cost = typeof costValue === 'number' ? costValue : null;
    const projectedValue = data[getForecastKey(activeTab)];
    const projectedCost =
      projectEndOfDayCost && typeof projectedValue === 'number'
        ? projectedValue
        : null;
    const hasProjectedCost =
      cost !== null && projectedCost !== null && cost < projectedCost;

    const renderMoney = (n: number) => (
      <Money amount={n} raw={n < 1} precision={getCurrencyPrecision(n)} />
    );

    return (
      <>
        <DataPopperRow
          dividerBottom
          left={
            <Typography variant="L10M">{`${costRate} cost per ${unit}`}</Typography>
          }
          right={
            <Stack direction="row" flexWrap="nowrap" gap={16}>
              <Typography variant="P10B" align="right" minWidth="50px">
                {dashesOrNum({ value: cost, mapNumber: renderMoney })}
              </Typography>
              {hasProjectedCost && projectedCost !== null ? (
                <Typography
                  variant="P10B"
                  align="right"
                  minWidth="50px"
                  sx={{ opacity: 0.5 }}
                >
                  {dashesOrNum({
                    value: projectedCost,
                    mapNumber: renderMoney,
                  })}
                </Typography>
              ) : null}
            </Stack>
          }
        />
        <DataPopperRow
          indicator={{
            color: normalizedCostColors[activeTab] ?? '',
          }}
          left={
            <Typography variant="L10R">
              {normalizedCostModeMap[activeTab]}
            </Typography>
          }
          right={
            <Stack direction="row" flexWrap="nowrap" gap={8}>
              <Typography variant="inherit" align="right">
                {dashesOrNum({ value: cost, mapNumber: renderMoney })}
              </Typography>
              {hasProjectedCost && projectedCost !== null ? (
                <Typography
                  variant="inherit"
                  align="right"
                  sx={{ opacity: 0.5 }}
                >
                  {dashesOrNum({
                    value: projectedCost,
                    mapNumber: renderMoney,
                  })}
                </Typography>
              ) : null}
            </Stack>
          }
        />
      </>
    );
  };

  return { tooltipHeader: TooltipHeader, tooltipFooter: TooltipFooter };
};
